import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, User, Send } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import { contactInfo, workWith, enquiryTypes } from "../mock/data";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const infoCards = [
  { icon: Phone, label: "Phone", value: contactInfo.phone, href: `tel:${contactInfo.phone.replace(/\s/g, "")}` },
  { icon: Mail, label: "Email", value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  { icon: MapPin, label: "Headquarters", value: contactInfo.hq, href: null },
  { icon: User, label: "Founder", value: contactInfo.founder, href: null },
];

const inputStyle = {
  width: "100%",
  background: "var(--oo-bg)",
  border: "1px solid var(--oo-border)",
  borderRadius: 12,
  padding: "14px 16px",
  color: "var(--oo-text)",
  fontSize: 15,
  outline: "none",
  transition: "border-color 0.2s ease",
};

const Field = ({ label, children }) => (
  <div>
    <label className="font-display" style={{ fontSize: 13, fontWeight: 600, color: "var(--oo-muted)", display: "block", marginBottom: 8 }}>{label}</label>
    {children}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We typically respond within 24 business hours.");
      setForm({ name: "", company: "", email: "", phone: "", type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const onFocus = (e) => (e.target.style.borderColor = "var(--oo-orange)");
  const onBlur = (e) => (e.target.style.borderColor = "var(--oo-border)");

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ paddingTop: 160, paddingBottom: 56 }}>
        <div className="glow-orb" style={{ width: 380, height: 380, background: "var(--oo-orange)", top: "-10%", right: "-6%", opacity: 0.18 }} />
        <div className="absolute inset-0 grid-overlay" style={{ opacity: 0.35 }} />
        <div className="oo-container relative">
          <Reveal><span className="oo-eyebrow">Contact</span></Reveal>
          <Reveal delay={80}>
            <h1 className="font-display font-extrabold" style={{ fontSize: "clamp(38px,6vw,76px)", lineHeight: 1.03, marginTop: 20, letterSpacing: "-0.02em" }}>
              Let's <span className="text-accent">Build</span> Together
            </h1>
          </Reveal>
        </div>
      </section>

      <section style={{ paddingBottom: 110 }}>
        <div className="oo-container">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr]">
            {/* LEFT: info */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {infoCards.map((c) => {
                  const Icon = c.icon;
                  const inner = (
                    <div className="oo-card h-full" style={{ padding: 24 }}>
                      <div className="inline-flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: 12, background: "var(--oo-orange-soft)" }}>
                        <Icon size={20} className="text-accent" />
                      </div>
                      <p className="oo-eyebrow" style={{ marginTop: 16, color: "var(--oo-faint)" }}>{c.label}</p>
                      <p className="font-display" style={{ fontSize: 16, fontWeight: 600, marginTop: 6 }}>{c.value}</p>
                    </div>
                  );
                  return (
                    <Reveal key={c.label}>
                      {c.href ? <a href={c.href}>{inner}</a> : inner}
                    </Reveal>
                  );
                })}
              </div>

              <Reveal delay={120}>
                <div style={{ marginTop: 32 }}>
                  <p className="oo-eyebrow" style={{ color: "var(--oo-faint)" }}>We Work With</p>
                  <div className="flex flex-wrap gap-3" style={{ marginTop: 16 }}>
                    {workWith.map((w) => (
                      <span key={w} className="font-display" style={{ fontSize: 14, fontWeight: 500, padding: "8px 16px", borderRadius: 999, border: "1px solid var(--oo-border)", color: "var(--oo-muted)" }}>
                        {w}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* RIGHT: form */}
            <Reveal delay={100}>
              <form onSubmit={onSubmit} className="oo-card" style={{ padding: "clamp(24px,4vw,44px)" }}>
                <h2 className="font-display" style={{ fontSize: 26, fontWeight: 700 }}>Send Us a Message</h2>
                <div className="grid gap-5 sm:grid-cols-2" style={{ marginTop: 28 }}>
                  <Field label="Your Name">
                    <input style={inputStyle} onFocus={onFocus} onBlur={onBlur} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Doe" />
                  </Field>
                  <Field label="Company">
                    <input style={inputStyle} onFocus={onFocus} onBlur={onBlur} value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Your company" />
                  </Field>
                  <Field label="Email">
                    <input type="email" style={inputStyle} onFocus={onFocus} onBlur={onBlur} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
                  </Field>
                  <Field label="Phone">
                    <input style={inputStyle} onFocus={onFocus} onBlur={onBlur} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 00000 00000" />
                  </Field>
                </div>
                <div style={{ marginTop: 20 }}>
                  <Field label="Type of Enquiry">
                    <Select value={form.type} onValueChange={(v) => update("type", v)}>
                      <SelectTrigger style={{ ...inputStyle, height: "auto" }}>
                        <SelectValue placeholder="Select a service..." />
                      </SelectTrigger>
                      <SelectContent>
                        {enquiryTypes.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div style={{ marginTop: 20 }}>
                  <Field label="Message">
                    <textarea rows={5} style={{ ...inputStyle, resize: "vertical" }} onFocus={onFocus} onBlur={onBlur} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your project..." />
                  </Field>
                </div>
                <button type="submit" disabled={submitting} className="btn-primary" style={{ marginTop: 28, width: "100%", justifyContent: "center", opacity: submitting ? 0.7 : 1, cursor: submitting ? "not-allowed" : "pointer" }}>
                  {submitting ? "Sending..." : "Send Message"} <Send size={16} />
                </button>
                <p style={{ color: "var(--oo-faint)", fontSize: 13, marginTop: 16, textAlign: "center" }}>
                  We typically respond within 24 business hours. For urgent queries, call directly.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
